import { Button, Card, Heading, IconButton, Input, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import {
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	OrganizationAccessLevel,
	type Payload,
	type ProcedureStep,
	type User,
} from "@overmap-ai/common"
import {
	APIError,
	selectLatestProcedureStepFieldsOfProcedureStep,
	selectProcedureStepById,
	selectProcedureStepDefaultAssigneesOfProcedureStep,
	selectProcedureStepDefaultReviewersOfProcedureStep,
	selectProcedureStepFieldsById,
	selectProcedureStepFieldsOfProcedureStep,
	selectUserById,
	selectUsersOfOrganization,
} from "@overmap-ai/core"
import { type FieldCondition, FieldConditionBuilder, useConditionManager } from "@overmap-ai/forms"
import { useSyncedState } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import React, { type FocusEventHandler, memo, useCallback, useMemo } from "react"

import { ColorButton } from "../../components/Buttons/ColorButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { AddUserDropdownMenu } from "../../components/DropdownMenus/AddUserDropdownMenu"
import { FieldsRevisionDropdownSelect } from "../../components/DropdownSelects/FieldsRevisionDropdownSelect"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { UserIcon } from "../../components/Icons/UserIcon"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ColorPickerPopover } from "../../components/Popovers/ColorPickerPopover"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { TITLE_MAX_LENGTH } from "../../constants"
import { useTextConfirmationDialog } from "../../contexts/hooks"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { getUserDisplayLabel } from "../../utils/users"

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.form,
		label: "Fields",
		description: "Manage procedure step fields",
		from: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/",
		to: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/fields",
	},
]

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/",
)

export const ProcedureStepSettingsDetailView = memo(() => {
	const { organizationId, procedureStepId } = routeApi.useParams()

	const procedureStep = useAppSelector(selectProcedureStepById(procedureStepId))

	if (!procedureStep) {
		throw new Error(`expected procedure step to be defined, got ${procedureStep}`)
	}

	const navigate = useNavigate()
	const openTextConfirmationDialog = useTextConfirmationDialog()
	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const users = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const procedureStepFields = useAppSelector(selectProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const latestProcedureStepFields = useAppSelector(selectLatestProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const procedureStepDefaultAssignees = useShallowEqaulSelector(
		selectProcedureStepDefaultAssigneesOfProcedureStep(procedureStep.uuid),
	)
	const procedureStepDefaultReviewers = useShallowEqaulSelector(
		selectProcedureStepDefaultReviewersOfProcedureStep(procedureStep.uuid),
	)

	const [selectedProcedureStepFieldsId, setSelectedProcedureStepFieldsId] = useSyncedState(
		latestProcedureStepFields?.uuid ?? "",
	)

	const selectedProcedureStepFields = useAppSelector(selectProcedureStepFieldsById(selectedProcedureStepFieldsId))

	const fieldSchema = useDeserializeFieldSchema(selectedProcedureStepFields?.fields ?? EMPTY_ARRAY)

	const conditions = useMemo(
		() => fieldSchema.deserializeConditions(selectedProcedureStepFields?.field_conditions ?? []),
		[fieldSchema, selectedProcedureStepFields?.field_conditions],
	)

	const nonAssigneeUsers = useMemo(() => {
		const userIds = new Set(procedureStepDefaultAssignees.map(({ user }) => user))
		return users.filter((user) => !userIds.has(user.id))
	}, [procedureStepDefaultAssignees, users])

	const nonReviewerUsers = useMemo(() => {
		const userIds = new Set(procedureStepDefaultReviewers.map(({ user }) => user))
		return users.filter((user) => !userIds.has(user.id))
	}, [procedureStepDefaultReviewers, users])

	const handlePatchProcedureStep = useCallback(
		(fields: Partial<Payload<ProcedureStep>>) => {
			const [_, promise] = sdk.procedureSteps.update({ uuid: procedureStep.uuid, ...fields })

			promise.catch((reason) => {
				toast.error({
					title: "Could not update step",
					description: reason instanceof APIError ? reason.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[procedureStep.uuid],
	)

	const handleConditionsChange = useCallback(
		(conditions: FieldCondition[]) => {
			if (!selectedProcedureStepFields?.uuid) return

			void sdk.procedureStepFields.update({
				uuid: selectedProcedureStepFields.uuid,
				field_conditions: fieldSchema.serializeConditions(conditions),
			})
		},
		[selectedProcedureStepFields?.uuid, fieldSchema],
	)

	const handleNameBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			handlePatchProcedureStep({ name: e.target.value })
		},
		[handlePatchProcedureStep],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(procedureStep.name, handleNameBlur, true)

	const handleStepColorChange = useCallback(
		(color: string) => {
			handlePatchProcedureStep({ color })
		},
		[handlePatchProcedureStep],
	)

	const handleAddDefaultAssignee = useCallback(
		(user: User) => {
			void sdk.procedureStepDefaultAssignees.add({
				procedure_step: procedureStep.uuid,
				user: user.id,
			})
		},
		[procedureStep.uuid],
	)

	const handleDeleteDefaultAssignee = useCallback((uuid: string) => {
		void sdk.procedureStepDefaultAssignees.delete(uuid)
	}, [])

	const handleAddDefaultReviewer = useCallback(
		(user: User) => {
			void sdk.procedureStepDefaultReviewers.add({
				procedure_step: procedureStep.uuid,
				user: user.id,
			})
		},
		[procedureStep.uuid],
	)

	const handleDeleteDefaultReviewer = useCallback((uuid: string) => {
		void sdk.procedureStepDefaultReviewers.delete(uuid)
	}, [])

	const conditionManager = useConditionManager(conditions, handleConditionsChange)

	const handleDelete = useCallback(() => {
		openTextConfirmationDialog((close) => ({
			title: `Delete ${procedureStep.name}`,
			description: "Are you sure you want to delete this step?",
			confirmString: "delete",
			confirmMessage: "Type 'delete' to confirm",
			actionLabel: "Delete",
			onClose: close,
			onConfirm: async () => {
				await navigate({
					from: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/",
					to: "/$organizationId/settings/procedure-types/$procedureTypeId/steps",
				})

				sdk.procedureSteps.delete(procedureStep.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete step",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		}))
	}, [navigate, openTextConfirmationDialog, procedureStep.name, procedureStep.uuid])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{procedureStep.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage procedure step settings.
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-2" size="sm">
					<div className="flex w-full items-center gap-2">
						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Color & name
							</Text>
							<div className="flex w-full gap-2">
								<ColorPickerPopover
									required
									color={procedureStep.color}
									onColorChange={handleStepColorChange}
									disabled={!isOrganizationAdmin}
									size="sm"
								>
									<ColorButton size="sm" radius="full" color={procedureStep.color} />
								</ColorPickerPopover>

								<Input.Root variant="outline">
									<Input.Field
										placeholder="Enter a name"
										maxLength={TITLE_MAX_LENGTH}
										disabled={!isOrganizationAdmin}
										{...nameInputProps}
									/>
								</Input.Root>
							</div>
						</div>
					</div>
				</Card>

				{selectedProcedureStepFields && (
					<Card className="flex w-full flex-col gap-4" size="sm">
						<div className="flex w-full justify-between gap-2">
							<Text>Conditions</Text>
							<FieldsRevisionDropdownSelect
								required
								fieldsRevisions={procedureStepFields}
								value={selectedProcedureStepFields}
								onValueChange={({ uuid }) => setSelectedProcedureStepFieldsId(uuid)}
								size="sm"
							>
								<Button size="xs" accentColor="base" variant="surface" className="w-max">
									Revision {selectedProcedureStepFields.revision}
									<LuIcon icon={modelIconMapping.chevronDown} />
								</Button>
							</FieldsRevisionDropdownSelect>
						</div>

						<Separator size="full" />

						<div className="flex w-full flex-col gap-2">
							<div className="flex w-full justify-between gap-2">
								<Text accentColor="base">Mark as completed when:</Text>
							</div>

							<FieldConditionBuilder
								fields={fieldSchema.getFields()}
								conditions={conditions}
								onAdd={conditionManager.addConditions.bind(conditionManager)}
								onReplace={conditionManager.replaceCondition.bind(conditionManager)}
								onRemove={conditionManager.removeCondition.bind(conditionManager)}
							/>
						</div>
					</Card>
				)}

				<RouteLinksCard routes={routes} />

				<Card className="flex flex-col gap-2" size="sm">
					<div className="flex items-center justify-between gap-2">
						<Text size="sm">Default assignees</Text>
						<AddUserDropdownMenu
							users={nonAssigneeUsers}
							onSelectUser={handleAddDefaultAssignee}
							disabled={!isOrganizationAdmin || nonAssigneeUsers.length === 0}
							size="sm"
						>
							<Button size="sm" accentColor="base" variant="soft" disabled={!isOrganizationAdmin}>
								<LuIcon icon={modelIconMapping.add} /> Add
							</Button>
						</AddUserDropdownMenu>
					</div>
					{procedureStepDefaultAssignees.length > 0 ? (
						<ul className="flex flex-col gap-2">
							{procedureStepDefaultAssignees.map((participant) => (
								<ProcedureStepParticipantListItem
									key={participant.uuid}
									participantUuid={participant.uuid}
									userId={participant.user}
									onDelete={handleDeleteDefaultAssignee}
									disabled={!isOrganizationAdmin}
								/>
							))}
						</ul>
					) : (
						<Text accentColor="base" size="sm" weight="regular">
							No default assignees.
						</Text>
					)}
				</Card>

				<Card className="flex flex-col gap-2" size="sm">
					<div className="flex items-center justify-between gap-2">
						<Text size="sm">Default reviewers</Text>
						<AddUserDropdownMenu
							users={nonReviewerUsers}
							onSelectUser={handleAddDefaultReviewer}
							disabled={!isOrganizationAdmin || nonReviewerUsers.length === 0}
							size="sm"
						>
							<Button size="sm" accentColor="base" variant="soft" disabled={!isOrganizationAdmin}>
								<LuIcon icon={modelIconMapping.add} /> Add
							</Button>
						</AddUserDropdownMenu>
					</div>

					{procedureStepDefaultReviewers.length > 0 ? (
						<ul className="flex flex-col gap-2">
							{procedureStepDefaultReviewers.map((participant) => (
								<ProcedureStepParticipantListItem
									key={participant.uuid}
									participantUuid={participant.uuid}
									userId={participant.user}
									onDelete={handleDeleteDefaultReviewer}
									disabled={!isOrganizationAdmin}
								/>
							))}
						</ul>
					) : (
						<Text accentColor="base" size="sm" weight="regular">
							No default reviewers.
						</Text>
					)}
				</Card>

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Button className="w-max" size="sm" accentColor="danger" onClick={handleDelete}>
						Delete
					</Button>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
ProcedureStepSettingsDetailView.displayName = "ProcedureStepSettingsDetailView"

interface ProcedureStepParticipantListItemProps {
	participantUuid: string
	userId: number
	onDelete: (uuid: string) => void
	disabled?: boolean
}

const ProcedureStepParticipantListItem = memo<ProcedureStepParticipantListItemProps>((props) => {
	const { participantUuid, userId, onDelete, disabled = false } = props

	const user = useAppSelector(selectUserById(userId))

	const handleDelete = useCallback(() => {
		onDelete(participantUuid)
	}, [onDelete, participantUuid])

	return (
		<Card role="listitem" variant="soft" className="flex justify-between gap-2" size="sm">
			<div className="flex min-w-0 grow items-center gap-2">
				<UserIcon userId={user?.id ?? -1} size="xs" />
				<Text size="sm" className="truncate whitespace-nowrap">
					{getUserDisplayLabel(user)}
					{user ? (
						<>
							&nbsp;&nbsp;
							<Text size="sm" accentColor="base">
								{user.email}
							</Text>
						</>
					) : null}
				</Text>
			</div>
			<IconButton accentColor="base" variant="ghost" size="sm" onClick={handleDelete} disabled={disabled}>
				<LuIcon icon={modelIconMapping.close} />
			</IconButton>
		</Card>
	)
})
ProcedureStepParticipantListItem.displayName = "ProcedureStepParticipantListItem"
